import React, { useState, useEffect } from 'react';
import { useIntl, FormattedMessage } from 'react-intl';
import { Collapse, TextField, Grid, Box } from '@material-ui/core';
import Autocomplete from '@material-ui/lab/Autocomplete';
import Switch from 'react-switch';
import * as Utils from '../../commons/utils/Utils.js';

const FILTER_STATE_INIT = {
  numeroDeParte: '',
  externalCode: '',
  descripcion: '',
  categoria: null,
  subcategoria: null,
  marca: null,
  showInactivos: false,
  soloBajoMinimo: false,
};

const InventarioItemsGridFiltros = (props) => {
  const intl = useIntl();
  const [filtersState, setFiltersState] = useState({ ...FILTER_STATE_INIT });

  const [categorias, setCategorias] = useState([]);
  const [subcategorias, setSubcategorias] = useState([]);
  const [marcas, setMarcas] = useState([]);

  // Los selects de categorías/subcategorías/marcas se reusan de Pañol2: son GET de solo
  // lectura y ya devuelven SelectOption ({value, label}). No se duplican endpoints ni se
  // modifica nada del módulo legacy.
  useEffect(() => {
    Utils.getData('/panol2/select/categorias')
      .then((data) => setCategorias(data || []))
      .catch(() => setCategorias([]));
    Utils.getData('/panol2/select/marcas')
      .then((data) => setMarcas(data || []))
      .catch(() => setMarcas([]));
  }, []);

  useEffect(() => {
    if (props.filterResetInputs) {
      setFiltersState({ ...FILTER_STATE_INIT });
      setSubcategorias([]);
      props.setFilterResetInputs && props.setFilterResetInputs(false);
    }
  }, [props.filterResetInputs]);

  useEffect(() => {
    !props.dropdownActive && props.setFilterResetInputs(false);
  }, [props.dropdownActive]);

  const triggerFilter = (state) => {
    const current = state !== undefined ? state : filtersState;
    props.handleFilterChange();
    props.setSearchActive(
      current.numeroDeParte !== '' ||
        current.externalCode !== '' ||
        current.descripcion !== '' ||
        !!current.categoria ||
        !!current.subcategoria ||
        !!current.marca ||
        current.soloBajoMinimo
    );
    props.setFilterQuery({
      numeroDeParte: current.numeroDeParte,
      externalCode: current.externalCode,
      descripcion: current.descripcion,
      categoria: current.categoria ? current.categoria.value : '',
      subcategoria: current.subcategoria ? current.subcategoria.value : '',
      marca: current.marca ? current.marca.value : '',
      showInactivos: current.showInactivos,
      soloBajoMinimo: current.soloBajoMinimo,
    });
  };

  const handleChange = (name, value) => {
    setFiltersState((prev) => ({ ...prev, [name]: value }));
  };

  const handleOnEnter = (e, name) => {
    if (e.keyCode === 13) {
      e.preventDefault();
      handleBuscar({ ...filtersState, [name]: e.target.value });
    }
  };

  const handleCategoriaChange = (value) => {
    setFiltersState((prev) => ({ ...prev, categoria: value, subcategoria: null }));
    if (value) {
      Utils.getData('/panol2/select/subcategorias?categoria=' + value.value)
        .then((data) => setSubcategorias(data || []))
        .catch(() => setSubcategorias([]));
    } else {
      setSubcategorias([]);
    }
  };

  const handleSwitch = (name, value) => {
    setFiltersState((prev) => ({ ...prev, [name]: value }));
  };

  const handleBuscar = (state) => {
    triggerFilter(state);
  };

  return (
    <Collapse in={props.dropdownActive}>
      <Grid container item xs={12} spacing={2} style={{ marginBottom: 16, marginTop: 4 }}>
        <Grid component={Box} item xs={12} sm={6} md={3} lg={2}>
          <TextField
            label={intl.formatMessage({
              id: 'inventarioItems.filtros.numero_de_parte',
              defaultMessage: 'Nº de parte',
            })}
            name="numeroDeParte"
            value={filtersState.numeroDeParte}
            onChange={(e) => handleChange('numeroDeParte', e.target.value)}
            onKeyDown={(e) => handleOnEnter(e, 'numeroDeParte')}
            className="col-12"
          />
        </Grid>
        <Grid component={Box} item xs={12} sm={6} md={3} lg={2}>
          <TextField
            label={intl.formatMessage({ id: 'inventarioItems.filtros.gtin', defaultMessage: 'GTIN' })}
            name="externalCode"
            value={filtersState.externalCode}
            onChange={(e) => handleChange('externalCode', e.target.value)}
            onKeyDown={(e) => handleOnEnter(e, 'externalCode')}
            className="col-12"
          />
        </Grid>
        <Grid component={Box} item xs={12} sm={6} md={3} lg={3}>
          <TextField
            label={intl.formatMessage({
              id: 'inventarioItems.filtros.descripcion',
              defaultMessage: 'Descripción',
            })}
            name="descripcion"
            value={filtersState.descripcion}
            onChange={(e) => handleChange('descripcion', e.target.value)}
            onKeyDown={(e) => handleOnEnter(e, 'descripcion')}
            className="col-12"
          />
        </Grid>
        <Grid component={Box} item xs={12} sm={6} md={3} lg={2}>
          <Autocomplete
            size="small"
            name="categoria"
            options={categorias}
            value={filtersState.categoria}
            getOptionLabel={(option) => (option ? option.label : '')}
            getOptionSelected={(option, value) => option.value === value.value}
            onChange={(_, value) => handleCategoriaChange(value)}
            renderInput={(params) => (
              <TextField
                {...params}
                variant="standard"
                label={intl.formatMessage({
                  id: 'inventarioItems.filtros.categoria',
                  defaultMessage: 'Categoría',
                })}
              />
            )}
          />
        </Grid>
        <Grid component={Box} item xs={12} sm={6} md={3} lg={2}>
          <Autocomplete
            size="small"
            name="subcategoria"
            options={subcategorias}
            value={filtersState.subcategoria}
            disabled={!filtersState.categoria}
            getOptionLabel={(option) => (option ? option.label : '')}
            getOptionSelected={(option, value) => option.value === value.value}
            onChange={(_, value) => handleChange('subcategoria', value)}
            renderInput={(params) => (
              <TextField
                {...params}
                variant="standard"
                label={intl.formatMessage({
                  id: 'inventarioItems.filtros.subcategoria',
                  defaultMessage: 'Subcategoría',
                })}
              />
            )}
          />
        </Grid>
        <Grid component={Box} item xs={12} sm={6} md={3} lg={2}>
          <Autocomplete
            size="small"
            name="marca"
            options={marcas}
            value={filtersState.marca}
            getOptionLabel={(option) => (option ? option.label : '')}
            getOptionSelected={(option, value) => option.value === value.value}
            onChange={(_, value) => handleChange('marca', value)}
            renderInput={(params) => (
              <TextField
                {...params}
                variant="standard"
                label={intl.formatMessage({ id: 'inventarioItems.filtros.marca', defaultMessage: 'Marca' })}
              />
            )}
          />
        </Grid>
        <Grid container item xs={12} spacing={2} alignItems="center">
          <Grid component={Box} item xs="auto" style={{ paddingRight: 20 }}>
            <div className="d-flex align-items-center" style={{ gap: 8 }}>
              <Switch
                onChange={(value) => handleSwitch('showInactivos', value)}
                checked={filtersState.showInactivos}
                offColor="#FF4961"
                onColor="#28D094"
              />
              <FormattedMessage id="inventarioItems.filtros.ver_inactivos" defaultMessage="Ver Inactivos" />
            </div>
          </Grid>
          <Grid component={Box} item xs="auto" style={{ paddingRight: 20 }}>
            <div className="d-flex align-items-center" style={{ gap: 8 }}>
              <Switch
                onChange={(value) => handleSwitch('soloBajoMinimo', value)}
                checked={filtersState.soloBajoMinimo}
                offColor="#FF4961"
                onColor="#28D094"
              />
              <FormattedMessage
                id="inventarioItems.filtros.solo_bajo_minimo"
                defaultMessage="Solo stock bajo mínimo"
              />
            </div>
          </Grid>
          <Grid component={Box} item style={{ flex: 1 }} />
          <Grid component={Box} item xs="auto">
            <button disabled={props.loading} className="btn btn-primary" onClick={() => handleBuscar()}>
              <i className="fa fa-search fa-xs"></i> <FormattedMessage id="Buscar" defaultMessage="Buscar" />
            </button>
          </Grid>
        </Grid>
      </Grid>
    </Collapse>
  );
};

export default InventarioItemsGridFiltros;
